import { useCallback, useEffect, useRef, useState } from 'react';

interface UseDrawerResult {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
}

export function useDrawer(): UseDrawerResult {
  const [isOpen, setIsOpen] = useState(false);
  const scrollYRef = useRef(0);
  const triggerRef = useRef<HTMLElement | null>(null);

  const open = useCallback(() => {
    scrollYRef.current = window.scrollY;
    triggerRef.current = document.activeElement as HTMLElement | null;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollYRef.current}px`;
    document.body.style.inlineSize = '100%';
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.inlineSize = '';
    window.scrollTo(0, scrollYRef.current);
    if (triggerRef.current && typeof triggerRef.current.focus === 'function') {
      triggerRef.current.focus();
    }
    setIsOpen(false);
  }, []);

  const toggle = useCallback(() => {
    if (isOpen) close();
    else open();
  }, [isOpen, open, close]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, close]);

  return { isOpen, open, close, toggle };
}
